import { searchItems } from "@/lib/search";
import { getAllNpcs, getAllLocations } from "@/lib/data";
import { getAllGuides } from "@/features/guides/registry";
import { getAllNews } from "@/features/news/registry";
import { ACHIEVEMENTS } from "@/features/achievements/data";
import { localizeAchievement } from "@/features/achievements/content";
import { KIND_ROUTE_SLUG } from "@/lib/routing";
import type { ItemKind } from "@/lib/data";
import type { Locale } from "@/lib/i18n/config";

export type SearchResultType = "item" | "npc" | "location" | "guide" | "news" | "achievement";

export interface GlobalSearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  href: string;
  kind?: string;
  isFallback?: boolean;
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

/** Combined search across the item catalog, NPCs, locations, guides, news and achievements. */
export function searchAll(query: string, locale: Locale, limitPerCategory = 4): GlobalSearchResult[] {
  const q = normalize(query);
  if (!q) return [];

  const results: GlobalSearchResult[] = [];

  for (const entry of searchItems(query, locale, limitPerCategory)) {
    const slug = KIND_ROUTE_SLUG[entry.kind as ItemKind] ?? "items";
    results.push({
      id: entry.id,
      type: "item",
      title: entry.displayName,
      subtitle: entry.kind,
      href: `/${slug}/${entry.id}`,
      kind: entry.kind,
      isFallback: entry.isNameFallback,
    });
  }

  getAllNpcs()
    .filter((npc) => normalize(npc.name).includes(q) || normalize(npc.role ?? "").includes(q))
    .slice(0, limitPerCategory)
    .forEach((npc) => {
      results.push({
        id: npc.id,
        type: "npc",
        title: npc.name,
        subtitle: npc.role ?? undefined,
        href: `/npcs/${npc.id}`,
      });
    });

  getAllLocations()
    .filter((location) => normalize(location.name).includes(q))
    .slice(0, limitPerCategory)
    .forEach((location) => {
      results.push({
        id: location.id,
        type: "location",
        title: location.name,
        subtitle: location.region ?? undefined,
        href: `/locations/${location.id}`,
      });
    });

  getAllGuides(locale)
    .filter((guide) => normalize(guide.meta.title).includes(q) || normalize(guide.meta.summary).includes(q))
    .slice(0, limitPerCategory)
    .forEach((guide) => {
      results.push({
        id: guide.meta.id,
        type: "guide",
        title: guide.meta.title,
        subtitle: guide.meta.summary,
        href: `/guides/${guide.meta.slug}`,
      });
    });

  getAllNews(locale)
    .filter((post) => normalize(post.meta.title).includes(q) || normalize(post.meta.excerpt).includes(q))
    .slice(0, limitPerCategory)
    .forEach((post) => {
      results.push({
        id: post.meta.id,
        type: "news",
        title: post.meta.title,
        subtitle: post.meta.excerpt,
        href: `/news/${post.meta.id}`,
      });
    });

  ACHIEVEMENTS.map((achievement) => localizeAchievement(achievement, locale))
    .filter(
      (achievement) => normalize(achievement.name).includes(q) || normalize(achievement.description ?? "").includes(q),
    )
    .slice(0, limitPerCategory)
    .forEach((achievement) => {
      results.push({
        id: achievement.id,
        type: "achievement",
        title: achievement.name,
        subtitle: achievement.hobby ?? undefined,
        href: `/achievements/${achievement.id}`,
      });
    });

  return results;
}

/**
 * What to show before the user has typed anything: a live taste of real
 * content (latest news/guides, a few NPCs and achievements) instead of a
 * fixed page-shortcut list — everything here is already loaded client-side,
 * so there's no need to hold back behind a "type to search" placeholder.
 */
export function getDiscoverResults(locale: Locale, limitPerCategory = 3): GlobalSearchResult[] {
  const results: GlobalSearchResult[] = [];

  getAllNews(locale)
    .slice(0, limitPerCategory)
    .forEach((post) => {
      results.push({
        id: post.meta.id,
        type: "news",
        title: post.meta.title,
        subtitle: post.meta.excerpt,
        href: `/news/${post.meta.id}`,
      });
    });

  getAllGuides(locale)
    .slice(0, limitPerCategory)
    .forEach((guide) => {
      results.push({
        id: guide.meta.id,
        type: "guide",
        title: guide.meta.title,
        subtitle: guide.meta.summary,
        href: `/guides/${guide.meta.slug}`,
      });
    });

  getAllNpcs()
    .slice(0, limitPerCategory)
    .forEach((npc) => {
      results.push({
        id: npc.id,
        type: "npc",
        title: npc.name,
        subtitle: npc.role ?? undefined,
        href: `/npcs/${npc.id}`,
      });
    });

  ACHIEVEMENTS.filter((a) => !a.isHidden)
    .map((achievement) => localizeAchievement(achievement, locale))
    .slice(0, limitPerCategory)
    .forEach((achievement) => {
      results.push({
        id: achievement.id,
        type: "achievement",
        title: achievement.name,
        subtitle: achievement.hobby ?? undefined,
        href: `/achievements/${achievement.id}`,
      });
    });

  return results;
}
